/**
 * Host dashboard — identity, concurrent room quota, org media defaults.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  fetchOrgQuota,
  updateOrgMediaPolicy,
} from "../api/signalingApi";
import {
  AUDIO_BITRATE_OPTIONS_KBPS,
  VIDEO_BITRATE_OPTIONS_KBPS,
  DEFAULT_AUDIO_BITRATE_KBPS,
  DEFAULT_VIDEO_BITRATE_KBPS,
} from "../webrtc/peerConnection";

export default function CustomerDashboard() {
  const [email, setEmail] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [quota, setQuota] = useState(null);
  const [quotaError, setQuotaError] = useState("");
  const [loadingQuota, setLoadingQuota] = useState(false);
  const [audioKbps, setAudioKbps] = useState(DEFAULT_AUDIO_BITRATE_KBPS);
  const [videoKbps, setVideoKbps] = useState(DEFAULT_VIDEO_BITRATE_KBPS);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyMessage, setPolicyMessage] = useState("");
  const [policyError, setPolicyError] = useState("");

  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  const loadQuota = useCallback(async () => {
    setLoadingQuota(true);
    setQuotaError("");
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload || {};
      setEmail(payload.email || "");
      setCompanyId(payload["custom:companyId"] || "");
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error("No ID token");
      const data = await fetchOrgQuota({ idToken: token });
      setQuota(data);
      if (data.audioKbps != null) setAudioKbps(Number(data.audioKbps));
      if (data.videoKbps != null) setVideoKbps(Number(data.videoKbps));
    } catch (err) {
      console.error("Failed to load quota", err);
      setQuotaError(err?.message || String(err));
    } finally {
      setLoadingQuota(false);
    }
  }, []);

  useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const saveMediaPolicy = useCallback(async () => {
    setSavingPolicy(true);
    setPolicyError("");
    setPolicyMessage("");
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error("No ID token");
      const data = await updateOrgMediaPolicy({
        idToken: token,
        audioKbps,
        videoKbps,
      });
      setAudioKbps(Number(data.audioKbps));
      setVideoKbps(Number(data.videoKbps));
      setQuota((q) =>
        q
          ? {
              ...q,
              audioKbps: data.audioKbps,
              videoKbps: data.videoKbps,
            }
          : q
      );
      setPolicyMessage(
        "Saved. New rooms will use these defaults; active rooms keep their current policy until changed in-room."
      );
    } catch (err) {
      console.error("Failed to save media policy", err);
      setPolicyError(err?.message || String(err));
    } finally {
      setSavingPolicy(false);
    }
  }, [audioKbps, videoKbps]);

  const audioOptions =
    quota?.audioBitrateOptions?.length > 0
      ? quota.audioBitrateOptions
      : AUDIO_BITRATE_OPTIONS_KBPS;
  const videoOptions =
    quota?.videoBitrateOptions?.length > 0
      ? quota.videoBitrateOptions
      : VIDEO_BITRATE_OPTIONS_KBPS;

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ marginTop: 0 }}>Host portal</h1>
      <p style={{ color: "#4b5563", lineHeight: 1.5 }}>
        Open rooms for your organization, share invite links with guests, and
        monitor how many rooms are active against your concurrent limit.
      </p>
      <div style={styles.card}>
        <div style={styles.row}>
          <span style={styles.label}>Email</span>
          <span>{email || "—"}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Company ID</span>
          <span>{companyId || "—"}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Account</span>
          <span>Host</span>
        </div>
      </div>

      <div style={{ ...styles.card, marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16 }}>Room quota</h2>
          <button type="button" onClick={loadQuota} disabled={loadingQuota} style={styles.btn}>
            {loadingQuota ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {quotaError ? (
          <p style={{ color: "#991b1b", margin: 0, fontSize: 14 }}>
            {quotaError}
            <br />
            <span style={{ color: "#6b7280" }}>
              Ensure the signaling server is running and{" "}
              <code>REACT_APP_SIGNALING_WS_URL</code> is set.
            </span>
          </p>
        ) : (
          <>
            <div style={styles.row}>
              <span style={styles.label}>Max concurrent rooms</span>
              <span style={styles.stat}>{quota?.maxRooms ?? "—"}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.label}>Currently active</span>
              <span style={styles.stat}>
                {quota ? quota.activeRooms : "—"}
                {quota ? ` / ${quota.maxRooms}` : ""}
              </span>
            </div>
            {quota?.rooms?.length ? (
              <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                <strong>Active rooms:</strong>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {quota.rooms.map((r) => (
                    <li key={r} style={{ fontFamily: "ui-monospace, monospace" }}>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>
                No rooms active right now.
              </p>
            )}
          </>
        )}
      </div>

      <div style={{ ...styles.card, marginTop: 16 }}>
        <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>Default media bitrates</h2>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280", lineHeight: 1.45 }}>
          Applied when a new room is opened. Clients enforce these caps locally.
          Hosts can still override live inside a room.
        </p>
        <div style={styles.row}>
          <span style={styles.label}>Audio</span>
          <label style={{ fontSize: 14 }}>
            <select
              value={audioKbps}
              onChange={(e) => setAudioKbps(Number(e.target.value))}
              style={styles.select}
            >
              {audioOptions.map((kbps) => (
                <option key={kbps} value={kbps}>
                  {kbps}
                </option>
              ))}
            </select>{" "}
            kbps
          </label>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Video</span>
          <label style={{ fontSize: 14 }}>
            <select
              value={videoKbps}
              onChange={(e) => setVideoKbps(Number(e.target.value))}
              style={styles.select}
            >
              {videoOptions.map((kbps) => (
                <option key={kbps} value={kbps}>
                  {kbps}
                </option>
              ))}
            </select>{" "}
            kbps
          </label>
        </div>
        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={saveMediaPolicy}
            disabled={savingPolicy || !!quotaError}
            style={styles.btnPrimary}
          >
            {savingPolicy ? "Saving…" : "Save defaults"}
          </button>
        </div>
        {policyError ? (
          <p style={{ color: "#991b1b", margin: "8px 0 0", fontSize: 13 }}>{policyError}</p>
        ) : null}
        {policyMessage ? (
          <p style={{ color: "#065f46", margin: "8px 0 0", fontSize: 13 }}>{policyMessage}</p>
        ) : null}
      </div>

      <p style={{ marginTop: 20 }}>
        <Link to="/room">Open Room →</Link>
      </p>
    </div>
  );
}

const styles = {
  card: {
    padding: 16,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
    fontSize: 14,
    alignItems: "center",
  },
  label: { fontWeight: 600, color: "#374151" },
  stat: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  select: {
    padding: "4px 8px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
  },
  btn: {
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#f3f4f6",
    cursor: "pointer",
    fontSize: 13,
  },
  btnPrimary: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #059669",
    background: "#059669",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
};
